<template>
	<div class="d-flex align-center mr-4">
		<NotificationHeaderChat
			v-if="$store.state.user.isAuthenticated"
			:clearTray="() => clearTray('chat')"
			size="x-small"
			:class="notificationClass" />
		<NotificationHeaderObservations
			v-if="$store.state.user.isAuthenticated"
			:clearTray="() => clearTray('contracts')"
			size="x-small"
			:class="notificationClass" />
		<NotificationsHeader
			v-if="$store.state.user.isAuthenticated"
			:clearTray="() => clearTray('regular')"
			:class="notificationClass" />
	</div>
</template>

<script>
import { initializeApp } from "firebase/app";
import { getDatabase, ref, onValue, off, set } from "firebase/database";

import _debounce from 'lodash/debounce'
import { eventBus } from '@/components/Helpers/Events/NotificationsEventBus'
import NotificationsHeader from '@/components/Helpers/Notifications/NotificationHeader'
import NotificationHeaderObservations from '@/components/Helpers/Notifications/NotificationHeaderObservations'
import NotificationHeaderChat from '@/components/Helpers/Notifications/NotificationHeaderChat'

export default {
	name: 'RTDBNotifications',
	components: { NotificationHeaderObservations, NotificationHeaderChat, NotificationsHeader },
	props: {
		noCompany: {
			type: Boolean,
			default: false
		}
	},
	filters: {
	},
	computed: {
		notificationClass() {
			return this.$vuetify.breakpoint.mobile ? '':'mr-2'
		},
		selectedCompany(){
			return this.$store.state.companies.selectedCompany
		},
		userInfo() {
			return this.$store.state.user.userInfo
		}
	},
	watch: {
		userInfo(newData, oldData){
			if(newData && newData != oldData){
				this.startRTDB()
			}
		},
		selectedCompany(newData, oldData){
			if(newData && newData != oldData){
				this.startRTDB()
			}
		}
	},
	data: () => ({
		app: null,
		database: null,
		notificationsRef: null,
		notificationRef: null,
		firebaseConfig: {
			apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
			authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
			databaseURL: process.env.VUE_APP_FIREBASE_RTDB_URL,
			projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
			storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
			messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
			appId: process.env.VUE_APP_FIREBASE_APP_ID
		}
	}),
	mounted(){
    eventBus.$on('deleteRTDBNotifications', (data) => {
			data.keys_to_remove.forEach((notificationKey) => {
				this.clearSingleNotification(data.type, notificationKey)
			})
    })
		this.init()
	},
	beforeDestroy() {
    this.stopNotificationsRTDBListener()
	},
	methods: {
		reloadInformationDebounced: _debounce(function() {
			eventBus.$emit('reloadInformation')
			eventBus.$emit('reloadChat')
			const notificationRef = ref(this.database, `notifications/${this.selectedCompany.company_id}/${this.userInfo.id}/general_update`)
			set(notificationRef, null)
		}, 1000),
		reloadChatDebounced: _debounce(function() {
			eventBus.$emit('reloadChat')
		}, 1000),
		init(){
			if (!this.app) {
				this.app = initializeApp(this.firebaseConfig)
				this.database = getDatabase(this.app)
				this.startRTDB()
			}
		},
		startRTDB(){
			if(this.userInfo && (this.selectedCompany || this.noCompany)){
				this.startNotificationsRTDBListenner(this.noCompany)
			}
		},
		startNotificationsRTDBListenner(noCompany){
			if(this.notificationsRef){
				this.stopNotificationsRTDBListener()
			}
			if(noCompany){
				this.notificationRef = `notifications/${this.userInfo.id}`
			}
			else if(this.selectedCompany){
				this.notificationRef = `notifications/${this.selectedCompany.company_id}/${this.userInfo.id}`
			}
			if(this.notificationRef){
				this.notificationsRef = ref(this.database, this.notificationRef)
			}
			onValue(this.notificationsRef, this.onNotificationsValue, (error) => console.error('Error RTDB Listener:', error))
		},
		onNotificationsValue(snapshot) {
			this.resetStore()
			if (!snapshot.exists()) return

			const rawNotifications = snapshot.val()
			this.$store.commit('notifications/setRTDBNotifications', rawNotifications)

			const flat = []
			const countsByType = {}

			Object.entries(rawNotifications).forEach(([type, group]) => {
				if (!group) return

				const list = Object.values(group)
				countsByType[type] = list.length
				this.setHasNotifications(type, list.length)

				list.forEach(notification => {
					flat.push({ type, notification })
				})
			})

			flat.sort(
				(a, b) => (a.notification.timestamp || 0) - (b.notification.timestamp || 0)
			)

			const {
				lastNotificationTimestamp,
				notificationsInitialized
			} = this.$store.state.notifications

			let notificationsToShow = []

			if (!notificationsInitialized) {
				notificationsToShow = flat.slice(-5)

				const lastTimestamp =
					notificationsToShow.at(-1)?.notification.timestamp || null

				this.$store.commit(
					'notifications/setLastNotificationTimestamp',
					lastTimestamp
				)
				this.$store.commit('notifications/setNotificationsInitialized', true)
			}
			else {
				notificationsToShow = flat.filter(
					({ notification }) =>
						notification.timestamp > lastNotificationTimestamp
				)

				const newest =
					notificationsToShow.at(-1)?.notification.timestamp || null

				if (newest) {
					this.$store.commit(
						'notifications/setLastNotificationTimestamp',
						newest
					)
				}
			}

			if (!notificationsToShow.length) return

			notificationsToShow.forEach(({ type, notification }) => {
				if (type !== 'general_update' && type !== 'force_reload') {
					this.setMessage(notification)
				}
				if(notification?.data?.force_reload){
					console.log('Enviando reload event')
					eventBus.$emit('reloadInformation')
				}

				this.notificationHandlers(type, notification)
			})
		},
		notificationHandlers(type, notification) {
			// console.log('[DEBUG] Handling notification:', type, notification)
			const handlers = {
				force_reload: () => {
					const forceRef = ref(this.database, this.notificationRef)
					set(forceRef, null)
					if(notification?.type == 'reload_permissions'){
						this.$store.dispatch('user/getUserInfo', {with_loading: false})
						return
					}
					setTimeout(() => window.location.reload(), 500)
				},

				contracts: () => {
					this.getUserInfo(notification)
					eventBus.$emit('reloadInformation', {
						contract_id: notification.data.contract_id,
						contract_content_id: notification.data.contract_content_id
					})
				},

				regular: () => {
					const idFields = ['budget_id', 'crm_id', 'report_id', 'user_request_id', 'additional_request_id', 'project_id']
					idFields.forEach(field => {
						if (notification.data?.[field]) {
							eventBus.$emit('reloadInformation', { [field]: notification.data[field] })
						}
						if(notification.data?.additional_request_id){
							eventBus.$emit('reloadInformationAdditionalRequirement', {id: notification.data.additional_request_id})
						}
					})
				},

				additional_request: () => {
					if(notification.data?.contract_id){
						eventBus.$emit('reloadInformation', { contract_id: notification.data.contract_id })
					}
					if(notification.data?.additional_request_id){
						eventBus.$emit('reloadInformationAdditionalRequirement', {id: notification.data.additional_request_id})
					}
				},

				general_update: () => {
					this.reloadInformationDebounced()
				},

				chat: () => {
					this.reloadChatDebounced()
				}
			}

			handlers[type]?.()
		},
		stopNotificationsRTDBListener() {
			if (this.notificationsRef) {
				off(this.notificationsRef)
				this.notificationsRef = null
			}
		},
		clearTray(path){
			const contractsRef = ref(this.database, `notifications/${this.selectedCompany.company_id}/${this.userInfo.id}/${path}`);
			set(contractsRef, null)
		},
		clearSingleNotification(path, notificationKey){
			const contractsRef = ref(this.database, `notifications/${this.selectedCompany.company_id}/${this.userInfo.id}/${path}/${notificationKey}`)
			set(contractsRef, null)
		},
		resetStore(){
			this.$store.commit('notifications/setRTDBNotifications', [])
			this.$store.commit('notifications/setHasChatNotifications', false)
			this.$store.commit('notifications/setHasContractNotifications', false)
			this.$store.commit('notifications/setHasRegularNotifications', false)
			this.$store.commit('notifications/setBadgeText', '--')
			this.$store.commit('notifications/setBadgeTextContract', '--')
			this.$store.commit('notifications/setBadgeTextChat', '--')
		},
		setHasNotifications(key, count){
			switch (key) {
				case 'chat':
					this.$store.commit('notifications/setHasChatNotifications', true)
					this.$store.commit('notifications/setBadgeTextChat', count || '--')
					this.$store.dispatch('notifications/getChatNotifications', {take: 10, types: [3]})
					break
				case 'contracts':
					this.$store.commit('notifications/setHasContractNotifications', true)
					this.$store.commit('notifications/setBadgeTextContract', count || '--')
					this.$store.dispatch('notifications/getContractNotifications', {take: 10, types: [4]})
					break
				case 'regular':
					this.$store.commit('notifications/setHasRegularNotifications', true)
					this.$store.commit('notifications/setBadgeText', count || '--')
					this.$store.dispatch('notifications/getNotifications', {take: 10, types: [1,2,5,6,7,8,9,10,11,12,13]})
					break
				default:
					break
			}
		},
		getUserInfo(notification_info){
			if(notification_info.data?.contract_revision){
				this.$store.dispatch('notifications/getContractNotifications', {take: 10, types: [4]})
			}
		},
    setMessage(messageData){
      const data = {
        message: messageData.message,
        title: messageData.title,
        created: new Date(),
				type: 'success',
				icon: 'mdi-check-circle',
				color: '#959595',
      }
      this.$store.dispatch('general/addMessage', data)
    }
	}
};
</script>